/**
 * Pump Control Panel — Interference-Aware Scheduling
 *
 * Shows:
 *   - Client-owned pump assets (planned, installed, active)
 *   - Interference matrix heatmap (NxN Theis drawdown)
 *   - Gantt chart schedule (24hr timeline per well)
 *   - Schedule summary + recommendations
 *   - Manual reading form
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, Legend,
} from 'recharts';
import { apiClient } from '../../api/client';

interface PumpControlPanelProps {
  siteId: string;
  aquifer: string;
  demandGalPerDay: number;
}

interface PumpAsset {
  id: string;
  well_name: string;
  latitude: number;
  longitude: number;
  well_depth_ft: number;
  ground_elevation_ft: number;
  pump_rate_gpm: number;
  pump_setting_ft: number | null;
  current_dtw_ft: number | null;
  current_sat_ft: number | null;
  controller_type: string;
  status: string;
  source: string;
  aquifer_name: string | null;
  install_date: string | null;
  recommendation_data: any;
}

interface ScheduleSlot {
  start: string;
  end: string;
  status: string;
  hours: number;
}

interface WellSchedule {
  well_id: string;
  well_name: string;
  pump_rate_gpm: number;
  slots: ScheduleSlot[];
  total_on_hours: number;
  total_extraction_gal: number;
  floyd_status: string;
  max_allowed_hours: number;
  conflicts_with: string[];
  raw_schedule: number[];
}

interface ConflictPair {
  well_a: string;
  well_b: string;
  distance_ft: number;
  max_interference_ft: number;
}

interface ScheduleResult {
  schedule_id?: string;
  summary: {
    total_water_gal: number;
    total_water_af: number;
    demand_target_gal: number;
    demand_met_pct: number;
    total_pump_hours: number;
    avg_pump_hours_per_well: number;
    max_simultaneous_wells: number;
    conflict_pairs_count: number;
    floyd_shutdowns: number;
    wells_scheduled: number;
  };
  schedule: WellSchedule[];
  interference: {
    matrix: number[][];
    conflict_pairs: ConflictPair[];
    conflict_threshold_ft: number;
    well_ids: string[];
    max_interference_ft: number;
  };
  recommendations: string[];
}

const FLOYD_COLORS: Record<string, string> = {
  healthy: '#10b981',
  watch: '#f59e0b',
  critical: '#ef4444',
  disabled: '#6b7280',
};

const STATUS_COLORS: Record<string, string> = {
  planned: '#8b5cf6',
  installed: '#f59e0b',
  active: '#10b981',
  disabled: '#6b7280',
  abandoned: '#374151',
};

export function PumpControlPanel({ siteId, aquifer, demandGalPerDay }: PumpControlPanelProps) {
  const [assets, setAssets] = useState<PumpAsset[]>([]);
  const [schedule, setSchedule] = useState<ScheduleResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [schedLoading, setSchedLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'assets' | 'schedule' | 'interference'>('assets');

  // Asset management
  const [editingAsset, setEditingAsset] = useState<string | null>(null);
  const [editGpm, setEditGpm] = useState(0);
  const [editStatus, setEditStatus] = useState('');

  const loadAssets = useCallback(async () => {
    try {
      const resp = await apiClient.get(`/phreatic/pump-control/assets/${siteId}`);
      setAssets(resp.data.assets || []);
    } catch (err) {
      console.warn('Failed to load pump assets:', err);
    }
  }, [siteId]);

  // Subscription state
  const [subscription, setSubscription] = useState<{ status: string; wells: any[]; well_count: number } | null>(null);
  const [subWellIds, setSubWellIds] = useState<Set<string>>(new Set());

  const loadSubscription = useCallback(async () => {
    try {
      const resp = await apiClient.get('/phreatic/billing/subscription');
      setSubscription(resp.data);
      const ids = new Set<string>((resp.data.wells || []).map((w: any) => String(w.well_id)));
      setSubWellIds(ids);
    } catch { /* billing not set up — allow all */ }
  }, []);

  useEffect(() => { loadAssets(); loadSubscription(); }, [loadAssets, loadSubscription]);

  const updateAsset = async (assetId: string, updates: Record<string, any>) => {
    try {
      await apiClient.put(`/phreatic/pump-control/assets/${assetId}`, updates);
      loadAssets();
      setEditingAsset(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Update failed');
    }
  };

  const deleteAsset = async (assetId: string) => {
    try {
      await apiClient.delete(`/phreatic/pump-control/assets/${assetId}`);
      loadAssets();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const generateSchedule = async () => {
    setSchedLoading(true);
    setError(null);
    try {
      const resp = await apiClient.post('/phreatic/pump-control/schedule/generate', {
        site_id: siteId,
        demand_gal_per_day: demandGalPerDay,
        aquifer,
        simulate: true,
      });
      setSchedule(resp.data);
      setView('schedule');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Schedule generation failed');
    } finally {
      setSchedLoading(false);
    }
  };

  // Manual well add state
  const [showAddWell, setShowAddWell] = useState(false);
  const [newWell, setNewWell] = useState({ name: '', lat: '', lon: '', depth: '300', gpm: '10', dtw: '' });
  const [addingWell, setAddingWell] = useState(false);

  const addManualWell = async () => {
    if (!newWell.name || !newWell.lat || !newWell.lon) return;
    setAddingWell(true);
    try {
      await apiClient.post('/phreatic/pump-control/assets', {
        site_id: siteId,
        well_name: newWell.name,
        latitude: parseFloat(newWell.lat),
        longitude: parseFloat(newWell.lon),
        well_depth_ft: parseFloat(newWell.depth) || 300,
        pump_rate_gpm: parseFloat(newWell.gpm) || 0,
        aquifer_name: aquifer,
        status: 'active',
        source: 'manual',
        current_dtw_ft: newWell.dtw ? parseFloat(newWell.dtw) : undefined,
      });
      setNewWell({ name: '', lat: '', lon: '', depth: '300', gpm: '10', dtw: '' });
      setShowAddWell(false);
      loadAssets();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to add well');
    } finally {
      setAddingWell(false);
    }
  };

  // Manual reading state
  const [showReading, setShowReading] = useState<string | null>(null);
  const [readingDtw, setReadingDtw] = useState('');
  const [submittingReading, setSubmittingReading] = useState(false);

  const submitReading = async (assetId: string) => {
    if (!readingDtw) return;
    setSubmittingReading(true);
    try {
      await apiClient.post('/phreatic/pump-control/telemetry/manual', {
        site_id: siteId,
        asset_id: assetId,
        depth_to_water_ft: parseFloat(readingDtw),
      });
      setShowReading(null);
      setReadingDtw('');
      loadAssets();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to submit reading');
    } finally {
      setSubmittingReading(false);
    }
  };

  const activeAssets = assets.filter(a => ['active', 'installed'].includes(a.status));
  const plannedAssets = assets.filter(a => a.status === 'planned');

  return (
    <div className="space-y-3">
      {/* View Toggle + Actions */}
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-3">
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {(['assets', 'schedule', 'interference'] as const).map(v => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1 text-[11px] rounded transition-colors ${
                  view === v ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40' : 'bg-mono-800 text-mono-400 border border-mono-700 hover:text-mono-300'
                }`}>
                {v === 'assets' ? `Well Assets (${assets.length})` : v === 'schedule' ? 'Pump Schedule' : 'Interference'}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={loadAssets}
              className="px-2 py-1 text-[10px] rounded bg-mono-800 text-mono-400 border border-mono-700 hover:text-mono-300">
              Refresh
            </button>
            <button onClick={() => setShowAddWell(!showAddWell)}
              className="px-2 py-1 text-[10px] rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30">
              + Add Well
            </button>
            {activeAssets.length >= 2 && (
              <button onClick={generateSchedule} disabled={schedLoading}
                className="px-3 py-1 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 disabled:opacity-50">
                {schedLoading ? 'Generating...' : 'Generate Schedule'}
              </button>
            )}
          </div>
        </div>
        {/* Add Well Form */}
        {showAddWell && (
          <div className="mt-2 bg-mono-800/50 rounded-lg p-3 border border-cyan-500/20">
            <div className="text-[10px] text-cyan-300 uppercase tracking-wider mb-2">Add Existing Well by Location</div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              <div>
                <label className="text-[9px] text-mono-500">Well Name</label>
                <input value={newWell.name} onChange={e => setNewWell({...newWell, name: e.target.value})}
                  placeholder="EW-1" className="w-full px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200" />
              </div>
              <div>
                <label className="text-[9px] text-mono-500">Latitude</label>
                <input value={newWell.lat} onChange={e => setNewWell({...newWell, lat: e.target.value})}
                  placeholder="36.345" className="w-full px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200" />
              </div>
              <div>
                <label className="text-[9px] text-mono-500">Longitude</label>
                <input value={newWell.lon} onChange={e => setNewWell({...newWell, lon: e.target.value})}
                  placeholder="-102.91" className="w-full px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200" />
              </div>
              <div>
                <label className="text-[9px] text-mono-500">Well Depth (ft)</label>
                <input value={newWell.depth} onChange={e => setNewWell({...newWell, depth: e.target.value})}
                  className="w-full px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200" />
              </div>
              <div>
                <label className="text-[9px] text-mono-500">Pump Rate (GPM)</label>
                <input value={newWell.gpm} onChange={e => setNewWell({...newWell, gpm: e.target.value})}
                  className="w-full px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200" />
              </div>
              <div className="flex items-end">
                <button onClick={addManualWell} disabled={addingWell || !newWell.name || !newWell.lat || !newWell.lon}
                  className="w-full px-2 py-1 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 disabled:opacity-50">
                  {addingWell ? 'Adding...' : 'Add to Schedule'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 text-red-400 text-xs">{error}</div>
      )}

      {/* ── ASSETS VIEW ── */}
      {view === 'assets' && (
        <div className="space-y-3">
          {assets.length === 0 ? (
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 text-center">
              <p className="text-mono-500 text-sm">No pump assets yet. Use the Well Intelligence tab to plan new wells.</p>
            </div>
          ) : (
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">
                Pump Assets ({assets.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-mono-400 border-b border-mono-800">
                      <th className="text-left py-1.5 px-2">Well</th>
                      <th className="text-center py-1.5 px-2">Status</th>
                      <th className="text-right py-1.5 px-2">Depth</th>
                      <th className="text-right py-1.5 px-2">GPM</th>
                      <th className="text-right py-1.5 px-2">DTW</th>
                      <th className="text-right py-1.5 px-2">Sat</th>
                      <th className="text-center py-1.5 px-2">Controller</th>
                      <th className="text-center py-1.5 px-2">Source</th>
                      <th className="text-center py-1.5 px-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.map(a => (<React.Fragment key={a.id}>
                      <tr className="border-b border-mono-800/50 text-mono-300">
                        <td className="py-1.5 px-2 font-medium">{a.well_name}</td>
                        <td className="text-center py-1.5 px-2">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px]"
                            style={{ backgroundColor: `${STATUS_COLORS[a.status]}20`, color: STATUS_COLORS[a.status] }}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[a.status] }} />
                            {a.status}
                          </span>
                        </td>
                        <td className="text-right py-1.5 px-2">{a.well_depth_ft} ft</td>
                        <td className="text-right py-1.5 px-2">
                          {editingAsset === a.id ? (
                            <input type="number" value={editGpm} onChange={e => setEditGpm(Number(e.target.value))}
                              className="w-16 px-1 py-0.5 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200" />
                          ) : (
                            <span className="text-cyan-400">{a.pump_rate_gpm}</span>
                          )}
                        </td>
                        <td className="text-right py-1.5 px-2">{a.current_dtw_ft != null ? `${a.current_dtw_ft} ft` : '—'}</td>
                        <td className="text-right py-1.5 px-2">{a.current_sat_ft != null ? `${a.current_sat_ft} ft` : '—'}</td>
                        <td className="text-center py-1.5 px-2 text-mono-500">{a.controller_type}</td>
                        <td className="text-center py-1.5 px-2 text-mono-500">{a.source === 'well_intelligence' ? 'AI' : 'Manual'}</td>
                        <td className="text-center py-1.5 px-2">
                          <div className="flex gap-1 justify-center">
                            {editingAsset === a.id ? (
                              <>
                                <button onClick={() => updateAsset(a.id, { pump_rate_gpm: editGpm, status: editStatus })}
                                  className="text-[9px] text-cyan-400 hover:text-cyan-300">Save</button>
                                <button onClick={() => setEditingAsset(null)}
                                  className="text-[9px] text-mono-500 hover:text-mono-400">Cancel</button>
                              </>
                            ) : (
                              <>
                                {a.status === 'planned' && (
                                  <button onClick={() => updateAsset(a.id, { status: 'installed' })}
                                    className="text-[9px] text-cyan-400 hover:text-cyan-300">Mark Installed</button>
                                )}
                                {a.status === 'installed' && (
                                  <button onClick={() => updateAsset(a.id, { status: 'active' })}
                                    className="text-[9px] text-cyan-400 hover:text-cyan-300">Activate</button>
                                )}
                                <button onClick={() => setShowReading(showReading === a.id ? null : a.id)}
                                  className="text-[9px] text-cyan-400 hover:text-cyan-300">Reading</button>
                                <button onClick={() => { setEditingAsset(a.id); setEditGpm(a.pump_rate_gpm); setEditStatus(a.status); }}
                                  className="text-[9px] text-cyan-400 hover:text-cyan-300">Edit</button>
                                <button onClick={() => deleteAsset(a.id)}
                                  className="text-[9px] text-red-400 hover:text-red-300">Del</button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {showReading === a.id && (
                        <tr className="bg-mono-800/30">
                          <td colSpan={9} className="px-2 py-2">
                            <div className="flex items-center gap-2">
                              <span className="text-[9px] text-mono-500 uppercase">DTW (ft):</span>
                              <input type="number" value={readingDtw} onChange={e => setReadingDtw(e.target.value)}
                                placeholder="e.g. 85.3" className="w-24 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200" />
                              <button onClick={() => submitReading(a.id)} disabled={submittingReading || !readingDtw}
                                className="px-2 py-1 text-[9px] rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 disabled:opacity-50">
                                {submittingReading ? 'Saving...' : 'Submit Reading'}
                              </button>
                              <span className="text-[9px] text-mono-600">Bridges to V5 predictor for updated inference</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>))}
                  </tbody>
                </table>
              </div>
              {plannedAssets.length > 0 && (
                <div className="mt-2 flex items-center justify-between">
                  <div className="text-[10px] text-mono-500">
                    {plannedAssets.length} planned well{plannedAssets.length > 1 ? 's' : ''} not yet in scheduling
                  </div>
                  <button
                    onClick={async () => {
                      for (const a of assets.filter(x => x.status !== 'active')) {
                        await apiClient.put(`/phreatic/pump-control/assets/${a.id}`, { status: 'active' });
                      }
                      loadAssets();
                    }}
                    className="px-2 py-1 text-[10px] rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30"
                  >
                    Activate All ({assets.filter(x => x.status !== 'active').length})
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── SCHEDULE VIEW ── */}
      {view === 'schedule' && schedule && (
        <div className="space-y-3">
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <SCard label="Demand Met" value={`${schedule.summary.demand_met_pct}%`}
              color={schedule.summary.demand_met_pct >= 100 ? 'green' : schedule.summary.demand_met_pct >= 80 ? 'amber' : 'red'} />
            <SCard label="Total Extraction" value={`${(schedule.summary.total_water_gal / 1e6).toFixed(2)}M gal`} color="cyan" />
            <SCard label="Pump Hours" value={`${schedule.summary.total_pump_hours} hr`} color="cyan" />
            <SCard label="Conflicts" value={`${schedule.summary.conflict_pairs_count} pairs`}
              color={schedule.summary.conflict_pairs_count > 0 ? 'amber' : 'green'} />
            <SCard label="Floyd Shutdowns" value={`${schedule.summary.floyd_shutdowns}`}
              color={schedule.summary.floyd_shutdowns > 0 ? 'red' : 'green'} />
          </div>

          {/* Gantt Chart */}
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">
              24-Hour Pump Schedule
            </h3>
            <div className="space-y-1.5">
              {/* Hour labels */}
              <div className="flex items-center">
                <div className="w-20 text-[9px] text-mono-500 text-right pr-2">Hour</div>
                <div className="flex-1 flex">
                  {Array.from({ length: 24 }, (_, h) => (
                    <div key={h} className="flex-1 text-center text-[8px] text-mono-600">
                      {h % 3 === 0 ? `${h}:00` : ''}
                    </div>
                  ))}
                </div>
              </div>

              {/* Well rows */}
              {schedule.schedule.map(ws => (
                <div key={ws.well_name} className="flex items-center group">
                  <div className="w-20 text-[10px] text-mono-300 text-right pr-2 truncate" title={ws.well_name}>
                    {ws.well_name}
                    <span className="ml-1 text-[8px]" style={{ color: FLOYD_COLORS[ws.floyd_status] }}>
                      {ws.floyd_status === 'disabled' ? 'OFF' : `${ws.total_on_hours}h`}
                    </span>
                  </div>
                  <div className="flex-1 flex h-5 bg-mono-800/50 rounded-sm overflow-hidden">
                    {ws.raw_schedule.map((on, slot) => {
                      const isConflict = !on && ws.conflicts_with.length > 0;
                      return (
                        <div
                          key={slot}
                          className="flex-1 transition-colors"
                          style={{
                            backgroundColor: ws.floyd_status === 'disabled' ? '#374151'
                              : on ? FLOYD_COLORS[ws.floyd_status]
                              : 'transparent',
                            opacity: on ? 0.7 : 0.1,
                          }}
                          title={`${ws.well_name} ${slot}:00 — ${on ? 'PUMPING' : 'OFF'}${isConflict ? ' (conflict avoidance)' : ''}`}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Legend */}
              <div className="flex items-center gap-4 mt-2 text-[9px] text-mono-400">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-2 rounded-sm" style={{ backgroundColor: FLOYD_COLORS.healthy, opacity: 0.7 }} />Pumping (healthy)
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-2 rounded-sm" style={{ backgroundColor: FLOYD_COLORS.watch, opacity: 0.7 }} />Pumping (watch)
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-2 rounded-sm" style={{ backgroundColor: FLOYD_COLORS.critical, opacity: 0.7 }} />Pumping (critical)
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-2 rounded-sm bg-mono-800" />Off / Recovery
                </span>
              </div>
            </div>
          </div>

          {/* Extraction per well */}
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">
              Daily Extraction by Well
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={schedule.schedule.filter(ws => ws.total_on_hours > 0)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="well_name" tick={{ fill: '#888', fontSize: 10 }} />
                <YAxis tick={{ fill: '#888', fontSize: 10 }}
                  label={{ value: 'Gallons', angle: -90, position: 'insideLeft', fill: '#666', fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', fontSize: 11, borderRadius: 6 }}
                  formatter={(val: number) => [`${val.toLocaleString()} gal`, 'Extraction']} />
                <Bar dataKey="total_extraction_gal" radius={[4, 4, 0, 0]}>
                  {schedule.schedule.filter(ws => ws.total_on_hours > 0).map((ws, i) => (
                    <Cell key={i} fill={FLOYD_COLORS[ws.floyd_status]} fillOpacity={0.7} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Recommendations */}
          {schedule.recommendations.length > 0 && (
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-2">
                Recommendations
              </h3>
              <ul className="space-y-1">
                {schedule.recommendations.map((rec, i) => (
                  <li key={i} className="text-xs text-mono-300 flex items-start gap-2">
                    <span className="text-cyan-400 mt-0.5">-</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {view === 'schedule' && !schedule && (
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 text-center">
          {subscription && subscription.status === 'none' && activeAssets.length > 0 ? (
            <div>
              <p className="text-mono-400 text-sm mb-2">Pump scheduling requires an active well subscription</p>
              <p className="text-mono-500 text-xs">$500/well/month — includes interference optimization, telemetry, and autonomous pump control</p>
            </div>
          ) : (
            <p className="text-mono-500 text-sm">
              {activeAssets.length < 2
                ? `Need at least 2 active wells to generate a schedule. Currently ${activeAssets.length} active.`
                : 'Click "Generate Schedule" to create an interference-aware pump schedule.'}
            </p>
          )}
        </div>
      )}

      {/* ── INTERFERENCE VIEW ── */}
      {view === 'interference' && schedule?.interference && (
        <div className="space-y-3">
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">
              Interference Matrix — Theis Drawdown (ft)
            </h3>
            <p className="text-[10px] text-mono-500 mb-3">
              Each cell shows how much drawdown well (row) causes at well (column) location after 12 hours of pumping.
              Threshold: {schedule.interference.conflict_threshold_ft} ft
            </p>
            <div className="overflow-x-auto">
              <table className="text-[10px]">
                <thead>
                  <tr>
                    <th className="px-2 py-1 text-mono-500"></th>
                    {schedule.interference.well_ids.map(id => (
                      <th key={id} className="px-2 py-1 text-mono-400 font-normal" style={{ writingMode: 'vertical-rl' }}>
                        {id}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {schedule.interference.matrix.map((row, i) => (
                    <tr key={i}>
                      <td className="px-2 py-1 text-mono-400 font-medium">{schedule.interference.well_ids[i]}</td>
                      {row.map((val, j) => {
                        const isConflict = val > schedule.interference.conflict_threshold_ft;
                        const intensity = Math.min(1, val / 5);
                        return (
                          <td key={j} className="px-2 py-1 text-center font-mono"
                            style={{
                              backgroundColor: i === j ? '#1a1a1a'
                                : isConflict ? `rgba(239, 68, 68, ${0.2 + intensity * 0.5})`
                                : val > 0 ? `rgba(6, 182, 212, ${intensity * 0.3})`
                                : 'transparent',
                              color: i === j ? '#444' : isConflict ? '#fca5a5' : '#888',
                            }}>
                            {i === j ? '-' : val.toFixed(1)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {schedule.interference.conflict_pairs.length > 0 && (
              <div className="mt-3 space-y-1">
                <div className="text-[10px] text-red-400 uppercase tracking-wider">Conflict Pairs (staggered in schedule)</div>
                {schedule.interference.conflict_pairs.map((cp, i) => (
                  <div key={i} className="text-[11px] text-mono-300">
                    <span className="text-red-400">{cp.well_a}</span> ↔ <span className="text-red-400">{cp.well_b}</span>
                    : {cp.distance_ft.toLocaleString()}ft apart, {cp.max_interference_ft}ft interference
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {view === 'interference' && !schedule?.interference && (
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 text-center">
          <p className="text-mono-500 text-sm">Generate a schedule first to see the interference matrix.</p>
        </div>
      )}
    </div>
  );
}

function SCard({ label, value, color }: { label: string; value: string; color: string }) {
  const colorMap: Record<string, string> = {
    cyan: 'text-cyan-400 border-cyan-500/30',
    green: 'text-cyan-400 border-cyan-500/30',
    amber: 'text-cyan-400 border-cyan-500/30',
    red: 'text-red-400 border-red-500/30',
  };
  const c = colorMap[color] || colorMap.cyan;
  return (
    <div className={`bg-mono-900 border rounded-lg p-2 ${c.split(' ')[1]}`}>
      <div className="text-[9px] text-mono-500 uppercase tracking-wider">{label}</div>
      <div className={`text-sm font-bold mt-0.5 ${c.split(' ')[0]}`}>{value}</div>
    </div>
  );
}
